(() => {
  const elementName = "wr-vat-price-toggle";

  if (customElements.get(elementName)) return;

  class WrVatPriceToggle extends HTMLElement {
    constructor() {
      super();
      this.handleChange = this.handleChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleVariantChangeStart = this.handleVariantChangeStart.bind(this);
      this.handleVariantChangeEnd = this.handleVariantChangeEnd.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.sectionRoot = this.parentElement?.closest("[data-section-id]");
      this.sectionId = this.dataset.sectionId || this.sectionRoot?.dataset.sectionId || "";
      this.fieldset = this.querySelector(".wr-vat-toggle__fieldset");
      this.note = this.querySelector("[data-wr-vat-note]");
      this.priceRoot = this.findPriceRoot();

      if (!this.sectionRoot || !this.fieldset || !this.note || !this.priceRoot) {
        this.hidden = true;
        return;
      }

      this.addEventListener("change", this.handleChange);
      this.sectionRoot.addEventListener("variantChangeStart", this.handleVariantChangeStart);
      this.sectionRoot.addEventListener("variantChangeEnd", this.handleVariantChangeEnd);

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      const storedView = this.readStoredView();
      const initialView = storedView || this.dataset.defaultView || "gross";
      this.setView(initialView, false);
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.handleChange);
      this.sectionRoot?.removeEventListener("variantChangeStart", this.handleVariantChangeStart);
      this.sectionRoot?.removeEventListener("variantChangeEnd", this.handleVariantChangeEnd);

      if (typeof this.unsubscribeVariantChange === "function") {
        this.unsubscribeVariantChange();
      }

      this.unsubscribeVariantChange = undefined;
      this.initialized = false;
    }

    findPriceRoot() {
      if (!this.sectionRoot || !this.sectionId) return null;
      return this.sectionRoot.querySelector(`[id="price-${this.sectionId}"]`);
    }

    handleChange(event) {
      const input = event.target.closest('.wr-vat-toggle__input[type="radio"]');
      if (!input || !this.contains(input) || !input.checked) return;
      this.setView(input.value, true);
    }

    handleVariantChangeStart() {
      this.setBusy(true);
    }

    handleVariantChangeEnd() {
      this.setBusy(false);
      this.hidden = Boolean(this.priceRoot?.classList.contains("visibility-hidden"));
    }

    handleVariantChange(payload) {
      const eventData = payload?.data;
      if (!eventData) return;

      const eventSectionId = String(eventData.sectionId || "");
      const originalSectionId = String(this.sectionRoot?.dataset.originalSectionId || "");
      if (eventSectionId !== this.sectionId && eventSectionId !== originalSectionId) return;

      const sourceSection = eventData.html?.getElementById(`shopify-section-${eventSectionId}`);
      const sourceToggle = sourceSection?.querySelector("[data-wr-vat-toggle]");

      if (sourceToggle) {
        this.copyPricingFrom(sourceToggle);
      } else if (eventData.variant) {
        this.updatePricingFromVariant(eventData.variant);
      }

      this.priceRoot = this.findPriceRoot();
      this.hidden = !this.priceRoot || this.priceRoot.classList.contains("visibility-hidden");
      this.setBusy(false);
      this.applyView();
    }

    copyPricingFrom(sourceToggle) {
      const pricingKeys = ["gross-current", "net-current", "gross-compare", "net-compare"];

      pricingKeys.forEach((key) => {
        const source = sourceToggle.querySelector(`[data-wr-price-template="${key}"]`);
        const destination = this.querySelector(`[data-wr-price-template="${key}"]`);
        if (source && destination) destination.innerHTML = source.innerHTML;
      });

      ["grossPrice", "netPrice", "grossCompareAtPrice", "netCompareAtPrice", "currency", "locale"].forEach(
        (key) => {
          if (sourceToggle.dataset[key] !== undefined) this.dataset[key] = sourceToggle.dataset[key];
        },
      );
    }

    updatePricingFromVariant(variant) {
      const grossPrice = Number(variant.price);
      const grossCompareAtPrice = Number(variant.compare_at_price || 0);
      const vatRate = Number(this.dataset.vatRate || 0);
      const multiplier = 1 + vatRate / 100;

      if (!Number.isFinite(grossPrice) || !Number.isFinite(multiplier) || multiplier <= 0) return;

      const netPrice = Math.round(grossPrice / multiplier);
      const netCompareAtPrice = grossCompareAtPrice > 0 ? Math.round(grossCompareAtPrice / multiplier) : 0;

      this.dataset.grossPrice = String(grossPrice);
      this.dataset.netPrice = String(netPrice);
      this.dataset.grossCompareAtPrice = String(grossCompareAtPrice);
      this.dataset.netCompareAtPrice = String(netCompareAtPrice);

      this.setTemplateText("gross-current", this.formatCents(grossPrice));
      this.setTemplateText("net-current", this.formatCents(netPrice));
      this.setTemplateText("gross-compare", grossCompareAtPrice > 0 ? this.formatCents(grossCompareAtPrice) : "");
      this.setTemplateText("net-compare", netCompareAtPrice > 0 ? this.formatCents(netCompareAtPrice) : "");
    }

    setTemplateText(key, value) {
      const template = this.querySelector(`[data-wr-price-template="${key}"]`);
      if (template) template.content.textContent = value;
    }

    formatCents(cents) {
      const locale = this.dataset.locale || document.documentElement.lang || "en";
      const currency = this.dataset.currency || "USD";
      const currencyDisplay = this.dataset.currencyCodeEnabled === "true" ? "code" : "symbol";

      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency,
          currencyDisplay,
        }).format(cents / 100);
      } catch (error) {
        return String(cents / 100);
      }
    }

    setView(view, shouldPersist) {
      const nextView = view === "net" ? "net" : "gross";
      const input = this.querySelector(`.wr-vat-toggle__input[value="${nextView}"]`);

      if (input) input.checked = true;
      this.dataset.wrView = nextView;

      if (shouldPersist) this.storeView(nextView);
      this.applyView();
    }

    applyView() {
      if (!this.priceRoot || this.priceRoot.classList.contains("visibility-hidden")) return;

      const view = this.dataset.wrView === "net" ? "net" : "gross";
      const currentPriceTemplate = this.querySelector(`[data-wr-price-template="${view}-current"]`);
      const comparePriceTemplate = this.querySelector(`[data-wr-price-template="${view}-compare"]`);

      if (currentPriceTemplate) {
        const currentPriceHtml = currentPriceTemplate.innerHTML.trim();
        this.priceRoot.querySelectorAll(".wt-product__price__final").forEach((price) => {
          price.innerHTML = currentPriceHtml;
        });
      }

      if (comparePriceTemplate) {
        const comparePriceHtml = comparePriceTemplate.innerHTML.trim();
        this.priceRoot.querySelectorAll(".wt-product__price__compare").forEach((price) => {
          price.innerHTML = comparePriceHtml;
        });
      }

      const noteText = view === "net" ? this.dataset.netNote || "" : this.dataset.grossNote || "";
      const showBusinessNote = this.dataset.showBusinessNote === "true";
      this.note.textContent = noteText;
      this.note.hidden = noteText.length === 0 || (view === "net" && !showBusinessNote);
    }

    setBusy(isBusy) {
      this.fieldset.disabled = isBusy;
      if (isBusy) {
        this.setAttribute("aria-busy", "true");
      } else {
        this.removeAttribute("aria-busy");
      }
    }

    storageKey() {
      return `wr-vat-price-view:${window.location.pathname}:${this.sectionId}:${this.dataset.blockId || ""}`;
    }

    readStoredView() {
      if (this.dataset.persistSelection !== "true") return null;

      try {
        const value = window.sessionStorage.getItem(this.storageKey());
        return value === "gross" || value === "net" ? value : null;
      } catch (error) {
        return null;
      }
    }

    storeView(view) {
      if (this.dataset.persistSelection !== "true") return;

      try {
        window.sessionStorage.setItem(this.storageKey(), view);
      } catch (error) {
        // The selector remains fully functional when storage is unavailable.
      }
    }
  }

  customElements.define(elementName, WrVatPriceToggle);
})();
